import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { usePostHog } from 'posthog-js/react'
import { useLocation } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import { toast } from 'sonner'
import { TabRefreshButton } from '@/components/panel/TabRefreshButton'
import { Button } from '@/components/ui/button'
import { CopyButton } from '@/components/ui/copy-button'
import { useCapability } from '@/features/permissions/hooks'
import { useAppNavigate } from '@/hooks/useAppNavigate'
import { sessionKeys } from '@/lib/queryKeys'
import { isRetryableSessionStatus } from '../retryableStatuses'
import {
  fetchSessionDetail,
  fetchSessionScreenshots,
  retrySession,
  updateSessionStatus,
} from '@/features/sessions/api'
import type {
  SessionAttemptRecord,
  SessionAttemptResult,
  SessionDetailResponse,
  SessionStatus,
  StructuredReport,
  UnexpectedErrorReportRecord,
} from '../types'
import { SessionDataTree } from './SessionDataTree'
import { SessionExtracts } from './SessionExtracts'
import { SessionMetadata } from './SessionMetadata'
import { StructuredRunReport } from './StructuredRunReport'
import { getSessionVariant } from '../sessionVariant'
import {
  UnexpectedErrorReportAction,
  UnexpectedErrorReportsSheet,
} from './UnexpectedErrorReportsSheet'

const statusColors: Record<SessionStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  running: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-600',
  user_completed: 'bg-green-100 text-green-800',
}

const STATUS_LABELS: Record<SessionStatus, string> = {
  pending: 'Pending',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
  user_completed: 'User Completed',
}

const attemptResultColors: Record<SessionAttemptResult, string> = {
  success: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-600',
  escalated: 'bg-orange-100 text-orange-800',
}

const getAttemptResultDisplay = (result: SessionAttemptResult | null) => {
  if (!result) {
    return {
      label: 'Pending',
      className: 'bg-gray-100 text-gray-600',
    }
  }
  return {
    label: result,
    className: attemptResultColors[result],
  }
}

interface LazyImageProps {
  src: string
  alt: string
  className?: string
}

function LazyImage({ src, alt, className }: LazyImageProps) {
  const [isLoaded, setIsLoaded] = useState(false)
  const imgRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsLoaded(true)
          observer.disconnect()
        }
      },
      { rootMargin: '100px' }
    )

    if (imgRef.current) {
      observer.observe(imgRef.current)
    }

    return () => observer.disconnect()
  }, [])

  return (
    <div ref={imgRef} className={className}>
      {isLoaded ? (
        <img src={src} alt={alt} className="w-full h-auto rounded" />
      ) : (
        <div className="w-full h-48 bg-gray-200 rounded animate-skeleton" />
      )}
    </div>
  )
}

interface SessionDetailProps {
  sessionId: string
}

function AttemptScreenshotsList({
  sessionId,
  attemptId,
  getToken,
}: {
  sessionId: string
  attemptId: string
  getToken: () => Promise<string | null>
}) {
  const pageSize = 8
  const [offset, setOffset] = useState(0)
  const [selectedScreenshotIndex, setSelectedScreenshotIndex] = useState(0)
  const shouldFetchScreenshots = Boolean(sessionId && attemptId)
  const { data, isLoading, isError } = useQuery({
    queryKey: sessionKeys.screenshots(sessionId, attemptId, pageSize, offset),
    queryFn: async () => {
      const token = await getToken()
      return fetchSessionScreenshots(token, sessionId, attemptId, {
        page_size: pageSize,
        offset,
      })
    },
    enabled: shouldFetchScreenshots,
    staleTime: 300000,
  })

  const screenshots = data?.data ?? []

  useEffect(() => {
    setOffset(0)
  }, [sessionId, attemptId])

  useEffect(() => {
    setSelectedScreenshotIndex(0)
  }, [sessionId, attemptId, offset, screenshots.length])

  if (!shouldFetchScreenshots) {
    return (
      <div className="text-sm text-gray-500">No screenshots available.</div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="text-sm text-gray-500">
        Unable to load screenshots for this attempt.
      </div>
    )
  }

  const hasPrev = data?.pagination?.has_prev === true
  const hasNext = data?.pagination?.has_next === true
  const totalScreenshots = data?.pagination?.total ?? 0
  const effectivePageSize = data?.pagination?.page_size ?? pageSize
  const effectiveOffset = data?.pagination?.offset ?? offset
  const totalPages =
    totalScreenshots > 0 ? Math.ceil(totalScreenshots / effectivePageSize) : 0
  const currentPage =
    totalScreenshots > 0
      ? Math.floor(effectiveOffset / effectivePageSize) + 1
      : 0

  if (!screenshots.length) {
    return (
      <div className="text-sm text-gray-500">No screenshots available.</div>
    )
  }

  const selectedScreenshot =
    screenshots[Math.min(selectedScreenshotIndex, screenshots.length - 1)]

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs text-gray-500">
          Page {currentPage} of {totalPages}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setOffset((prev) => Math.max(0, prev - pageSize))}
            disabled={!hasPrev}
            className="px-3 py-1.5 text-xs rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed">
            Previous
          </button>
          <button
            type="button"
            onClick={() => setOffset((prev) => prev + pageSize)}
            disabled={!hasNext}
            className="px-3 py-1.5 text-xs rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed">
            Next
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {screenshots.map((screenshot, index) => (
          <button
            key={index}
            type="button"
            onClick={() => setSelectedScreenshotIndex(index)}
            className={`overflow-hidden rounded border transition-[color,background-color,border-color,box-shadow,opacity] ${
              index === selectedScreenshotIndex
                ? 'ring-2 ring-blue-500 border-blue-300'
                : 'border-gray-200 hover:border-gray-300'
            }`}>
            <img
              src={screenshot.url}
              alt={`Screenshot thumbnail ${screenshot.sequence}`}
              className="h-24 w-full object-cover"
            />
          </button>
        ))}
      </div>

      {selectedScreenshot && (
        <div className="pt-2">
          <div className="text-sm text-gray-500 mb-3">
            <span className="font-medium">#{selectedScreenshot.sequence}</span>
            <span className="mx-2">•</span>
            <span className="font-mono text-xs">{selectedScreenshot.name}</span>
          </div>
          <LazyImage
            src={selectedScreenshot.url}
            alt={`Screenshot ${selectedScreenshot.sequence}`}
            className="max-w-full"
          />
        </div>
      )}
    </div>
  )
}

// All statuses that might exist in data (for display)
const ALL_STATUSES: SessionStatus[] = [
  'pending',
  'running',
  'completed',
  'failed',
  'cancelled',
  'user_completed',
]

// Statuses that are allowed to be *set* from the UI. "pending" is not one of
// them: requeuing a session has to clear Nebula's dispatch bookkeeping, so it
// goes through the Retry button (retrySession) rather than a status write. See
// retry_session_handler in hoku/server/handlers/sessions.py.
const EDITABLE_STATUSES = new Set(['user_completed', 'cancelled'])
const EMPTY_UNEXPECTED_ERROR_REPORTS: UnexpectedErrorReportRecord[] = []

export function SessionDetail({ sessionId }: SessionDetailProps) {
  const { getToken } = useAuth()
  const posthog = usePostHog()
  // Each section is its own row. The server blanks what the caller may not
  // read, and these decide what the page still offers to label.
  const canSeeCost = useCapability('sessionDetail.cost') === true
  const canChangeStatus = useCapability('sessionDetail.status') === true
  const canSeeAttempts = useCapability('sessionDetail.attempts') === true
  const canSeeScreenshots = useCapability('sessionDetail.screenshots') === true
  const queryClient = useQueryClient()
  const location = useLocation()
  const { toTask } = useAppNavigate()
  const [expandedPreviousAttemptIds, setExpandedPreviousAttemptIds] = useState<
    Set<string>
  >(new Set())
  const [reportAttemptId, setReportAttemptId] = useState<string | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const { data, isLoading, error } = useQuery({
    queryKey: sessionKeys.detail(sessionId),
    queryFn: async () => {
      const token = await getToken()
      return fetchSessionDetail(token, sessionId!)
    },
    enabled: !!sessionId,
    staleTime: 300000, // 5 minutes
  })

  /**
   * Pull the page's data again on demand.
   *
   * Both queries hold for five minutes, which is the wrong cadence for a
   * session still being worked — status, report and extracts all land after the
   * page is already open. Invalidating rather than calling refetch() also
   * covers the attempt screenshots, whose queries live in child components:
   * the key prefix catches every page/offset of them, and the collapsed ones
   * refetch when they next mount instead of all at once.
   */
  const refreshSession = async () => {
    setIsRefreshing(true)
    try {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: sessionKeys.detail(sessionId),
          refetchType: 'active',
        }),
        queryClient.invalidateQueries({
          queryKey: sessionKeys.screenshotsAll(sessionId),
          refetchType: 'active',
        }),
      ])
    } finally {
      setIsRefreshing(false)
    }
  }

  const statusMutation = useMutation({
    mutationFn: async (newStatus: SessionStatus) => {
      const token = await getToken()
      return updateSessionStatus(token, sessionId, newStatus)
    },
    onSuccess: (_, newStatus) => {
      queryClient.setQueryData(
        sessionKeys.detail(sessionId),
        (old: SessionDetailResponse | undefined) => {
          if (!old) return old
          return { ...old, session: { ...old.session, status: newStatus } }
        }
      )
    },
  })

  // Requeues through Nebula, which clears the dispatch bookkeeping a rerun
  // needs. Writing "pending" straight to the status column instead leaves a
  // Cloud-Run-executed session with no dispatch ever fired.
  const retryMutation = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      return retrySession(token, sessionId)
    },
    onSuccess: async () => {
      toast.success('Session retry started')
      await queryClient.invalidateQueries({
        queryKey: sessionKeys.detail(sessionId),
      })
    },
    onError: (retryError) => {
      toast.error(
        retryError instanceof Error
          ? retryError.message
          : 'Failed to retry session'
      )
    },
  })

  const session = data?.session
  const reportsByAttempt = useMemo(() => {
    const grouped = new Map<string, UnexpectedErrorReportRecord[]>()
    for (const report of data?.unexpected_error_reports ?? []) {
      const attemptReports = grouped.get(report.attempt_id) ?? []
      attemptReports.push(report)
      grouped.set(report.attempt_id, attemptReports)
    }
    return grouped
  }, [data?.unexpected_error_reports])
  const selectedAttemptReports = reportAttemptId
    ? (reportsByAttempt.get(reportAttemptId) ?? EMPTY_UNEXPECTED_ERROR_REPORTS)
    : EMPTY_UNEXPECTED_ERROR_REPORTS
  const openUnexpectedErrorReports = (
    attemptId: string,
    reports: UnexpectedErrorReportRecord[]
  ) => {
    posthog?.capture('unexpected_error_reports_viewed', {
      session_id: sessionId,
      attempt_id: attemptId,
      report_ids: reports.map((report) => report.id),
      report_count: reports.length,
    })
    setReportAttemptId(attemptId)
  }
  const factsEntries =
    session?.facts_json &&
    typeof session.facts_json === 'object' &&
    !Array.isArray(session.facts_json)
      ? Object.entries(session.facts_json)
      : []
  const attempts = [...(data?.attempts ?? [])].toSorted((a, b) => {
    const aTime = new Date(a.started_at ?? a.completed_at ?? 0).getTime()
    const bTime = new Date(b.started_at ?? b.completed_at ?? 0).getTime()
    return bTime - aTime
  })
  const hasAttempts = attempts.length > 0
  const latestAttempt = attempts[0]
  const latestAttemptVideoUrl = latestAttempt
    ? data?.media?.[latestAttempt.id]?.video?.url
    : undefined
  const previousAttempts = attempts.slice(1)
  const legacyScreenshots = data?.legacy_media?.screenshots ?? []
  // The recording answers to its own row, so it has to be reachable when the
  // attempts are not. It is filed under an attempt id, and the server empties
  // the attempt list without touching the media keyed by it — so with the
  // attempts withheld there is no attempt left to look it up through.
  const attemptlessVideoUrl = hasAttempts
    ? undefined
    : (Object.values(data?.media ?? {}).find((media) => media?.video?.url)
        ?.video?.url ?? data?.legacy_media?.video?.url)

  useEffect(() => {
    const attemptIdFromAnchor = location.hash.replace(/^#/, '')
    if (!attemptIdFromAnchor) return

    const matchingAttempt = (data?.attempts ?? []).find(
      (attempt) => attempt.id === attemptIdFromAnchor
    )
    if (!matchingAttempt) return

    setExpandedPreviousAttemptIds((prev) => {
      if (prev.has(attemptIdFromAnchor)) return prev
      const next = new Set(prev)
      next.add(attemptIdFromAnchor)
      return next
    })
  }, [location.hash, data?.attempts])

  useEffect(() => {
    const attemptIdFromAnchor = location.hash.replace(/^#/, '')
    if (!attemptIdFromAnchor) return

    const raf = window.requestAnimationFrame(() => {
      const target = document.getElementById(attemptIdFromAnchor)
      if (!target) return
      target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })

    return () => window.cancelAnimationFrame(raf)
  }, [location.hash, data?.attempts])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  if (error && !session) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-sm text-red-500">
          Error loading session details: {error.message}
        </div>
      </div>
    )
  }

  if (!session) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-sm text-gray-500">Session not found.</div>
      </div>
    )
  }

  const showSourceId = Boolean(session.metadata?.source_id)
  const showSourceType = Boolean(session.metadata?.source_type)
  const sessionVariant = getSessionVariant(session.metadata)
  const headerMetadataKeys = [
    ...(showSourceId ? ['source_id'] : []),
    ...(showSourceType ? ['source_type'] : []),
    ...(sessionVariant ? ['variant_key'] : []),
  ]

  const formatDateTime = (value: string | null) => {
    if (!value) return 'N/A'
    return new Date(value).toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    })
  }

  const formatDuration = (
    startedAt: string | null,
    completedAt: string | null
  ) => {
    if (!startedAt || !completedAt) return 'N/A'
    const start = new Date(startedAt)
    const end = new Date(completedAt)
    const durationMs = end.getTime() - start.getTime()
    const durationSec = Math.round(durationMs / 1000)
    if (durationSec < 60) return `${durationSec}s`
    const durationMin = Math.round(durationSec / 60)
    if (durationMin < 60) return `${durationMin}m`
    const durationHr = Math.floor(durationMin / 60)
    const remainMin = durationMin % 60
    return `${durationHr}h ${remainMin}m`
  }

  const formatSessionTypeName = (slug: string | null) => {
    if (!slug) return 'N/A'
    return slug
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const markdownComponents = {
    h1: ({ node: _node, ...props }: any) => (
      <h1 className="text-2xl font-semibold mt-8 mb-4" {...props} />
    ),
    h2: ({ node: _node, ...props }: any) => (
      <h2 className="text-xl font-semibold mt-6 mb-3" {...props} />
    ),
    h3: ({ node: _node, ...props }: any) => (
      <h3 className="text-lg font-semibold mt-4 mb-2" {...props} />
    ),
    p: ({ node: _node, ...props }: any) => (
      <p className="my-4 leading-7 text-gray-800" {...props} />
    ),
    ul: ({ node: _node, ...props }: any) => (
      <ul className="my-4 space-y-2 list-disc list-inside" {...props} />
    ),
    ol: ({ node: _node, ...props }: any) => (
      <ol className="my-4 space-y-2 list-decimal list-inside" {...props} />
    ),
    li: ({ node: _node, ...props }: any) => <li className="my-2" {...props} />,
    strong: ({ node: _node, ...props }: any) => (
      <strong className="font-semibold" {...props} />
    ),
    code: ({ node: _node, className, ...props }: any) =>
      !className ? (
        <code
          className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono"
          {...props}
        />
      ) : (
        <code
          className="block bg-gray-100 p-4 rounded my-4 text-sm font-mono overflow-x-auto"
          {...props}
        />
      ),
  }

  const buildLogfireUrl = ({
    traceId,
    spanId,
    completedAt,
    startedAt,
  }: {
    traceId: string | null
    spanId?: string | null
    completedAt?: string | null
    startedAt?: string | null
  }) => {
    if (!traceId) return null

    const query = spanId
      ? `trace_id='${traceId}' and span_id='${spanId}'`
      : `trace_id='${traceId}'`

    const referenceRaw = completedAt ?? startedAt
    let timeWindow = '1h'
    if (referenceRaw) {
      const referenceTime = new Date(referenceRaw)
      const now = new Date()
      const hoursSinceReference = Math.ceil(
        (now.getTime() - referenceTime.getTime()) / (1000 * 60 * 60)
      )

      if (hoursSinceReference <= 1) timeWindow = '1h'
      else if (hoursSinceReference <= 6) timeWindow = '6h'
      else if (hoursSinceReference <= 24) timeWindow = '24h'
      else if (hoursSinceReference <= 168) timeWindow = '7d'
      else timeWindow = '30d'
    }

    const spanParam = spanId ? `&spanId=${spanId}` : ''
    return `https://logfire-us.pydantic.dev/helpcare/nova?traceId=${traceId}${spanParam}&q=${encodeURIComponent(query)}&last="${timeWindow}"`
  }

  const getLogfireUrl = () =>
    buildLogfireUrl({
      traceId: session.logfire_trace_id,
      spanId: session.logfire_span_id,
      completedAt: session.completed_at,
      startedAt: session.started_at,
    })

  const getAttemptLogfireUrl = (attempt: SessionAttemptRecord) =>
    buildLogfireUrl({
      traceId: attempt.logfire_trace_id,
      spanId: attempt.logfire_span_id,
      completedAt: attempt.completed_at,
      startedAt: attempt.started_at,
    })

  const renderAttemptTopInfo = (
    attempt: SessionAttemptRecord,
    options?: { includeLogfireTrace?: boolean }
  ) => {
    const attemptResultDisplay = getAttemptResultDisplay(attempt.result)
    const attemptLogfireUrl = getAttemptLogfireUrl(attempt)
    const includeLogfireTrace = options?.includeLogfireTrace ?? true

    return (
      <div className="space-y-2">
        <div className="group flex items-center gap-2">
          <span className="text-xs text-gray-500 w-24 shrink-0">
            Attempt ID:
          </span>
          <span className="font-mono text-xs break-all text-gray-900">
            {attempt.id}
          </span>
          <CopyButton content={attempt.id} label="Attempt ID" />
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 w-24 shrink-0">Result:</span>
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              attemptResultDisplay.className
            }`}>
            {attemptResultDisplay.label}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 w-24 shrink-0">Duration:</span>
          <span className="text-xs font-semibold text-gray-900">
            {formatDuration(attempt.started_at, attempt.completed_at)}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 w-24 shrink-0">
            Tokens used:
          </span>
          <span className="text-xs text-gray-900">
            {attempt.token_usage != null
              ? attempt.token_usage.toLocaleString('en-US')
              : '-'}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 w-24 shrink-0">Cost:</span>
          <span className="text-xs text-gray-900">
            {attempt.token_cost != null
              ? `$${attempt.token_cost.toFixed(4)}`
              : '-'}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 w-24 shrink-0">
            Started at:
          </span>
          <span className="text-xs text-gray-900">
            {formatDateTime(attempt.started_at)}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 w-24 shrink-0">
            Completed at:
          </span>
          <span className="text-xs text-gray-900">
            {formatDateTime(attempt.completed_at)}
          </span>
        </div>

        {includeLogfireTrace && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-24 shrink-0">
              Logfire Trace:
            </span>
            {attemptLogfireUrl ? (
              <a
                href={attemptLogfireUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
                View trace
                <svg
                  className="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </a>
            ) : (
              <span className="text-xs text-gray-500">-</span>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      <div className="relative max-w-7xl mx-auto px-6 py-8">
        <div className="absolute right-6 top-6">
          <TabRefreshButton
            onRefresh={refreshSession}
            isFetching={isRefreshing}
            disabled={statusMutation.isPending}
            variant="outline"
            size="icon"
            iconClassName="size-4"
          />
        </div>
        {/* Metadata Section - Top */}
        <div className="mb-8 space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div className="group flex items-center gap-2">
              <span className="text-xs text-gray-500">Session ID:</span>
              <div className="font-mono text-xs break-all text-gray-900">
                {session.id}
              </div>
              <CopyButton
                content={session.id}
                label="Session ID"
                className="opacity-0 group-hover:opacity-100 hover:text-gray-600 transition-opacity"
              />
            </div>
            <div className="flex shrink-0 items-center gap-3 pr-12">
              {error && session && (
                <span role="alert" className="text-xs text-red-500">
                  Refresh failed: {error.message}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Type:</span>
            <span className="text-xs text-gray-900">
              {formatSessionTypeName(session.slug)}
            </span>
          </div>

          {sessionVariant && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Variant:</span>
              <code className="font-mono text-xs text-gray-900">
                {sessionVariant}
              </code>
            </div>
          )}

          {session.patient_id && (
            <div className="group flex items-center gap-2">
              <span className="text-xs text-gray-500">Client ID:</span>
              <div className="font-mono text-xs break-all text-gray-900">
                {session.patient_id}
              </div>
              <CopyButton
                content={session.patient_id}
                label="Client ID"
                className="opacity-0 group-hover:opacity-100 hover:text-gray-600 transition-opacity"
              />
            </div>
          )}

          {session.duty_id && (
            <div className="group flex items-center gap-2">
              <span className="text-xs text-gray-500">Duty ID:</span>
              <div className="font-mono text-xs break-all text-gray-900">
                {session.duty_id}
              </div>
              <CopyButton
                content={session.duty_id}
                label="Duty ID"
                className="opacity-0 group-hover:opacity-100 hover:text-gray-600 transition-opacity"
              />
            </div>
          )}

          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Created at:</span>
            <span className="text-xs">
              {formatDateTime(session.created_at)}
            </span>
          </div>

          {/* A run that spent nothing has nothing to report, so zero reads as
              noise under a heading rather than a figure worth the row. */}
          {canSeeCost && !!session.token_usage && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Total tokens:</span>
              <span className="text-xs">
                {session.token_usage.toLocaleString('en-US')}
              </span>
            </div>
          )}

          {canSeeCost && !!session.token_cost && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Total cost:</span>
              <span className="text-xs">${session.token_cost.toFixed(4)}</span>
            </div>
          )}

          {/* Inline metadata fields */}
          {session.metadata && Object.keys(session.metadata).length > 0 && (
            <>
              {showSourceId && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Source ID:</span>
                  <div className="font-mono text-xs break-all text-gray-900">
                    {String(session.metadata.source_id)}
                  </div>
                  {String(session.metadata.source_type) === 'call' && (
                    <button
                      type="button"
                      onClick={() =>
                        toTask(
                          `/calls?sort_by=created_at&sort_order=desc&details=1&call_id=${String(
                            session.metadata!.source_id
                          )}`
                        )
                      }
                      className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline cursor-pointer transition-colors">
                      View call
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              )}
              {showSourceType && (
                <div className="group flex items-center gap-2">
                  <span className="text-xs text-gray-500">Source Type:</span>
                  <span className="text-xs text-gray-900">
                    {String(session.metadata.source_type)}
                  </span>
                </div>
              )}
            </>
          )}

          {/* Logfire Trace Link */}
          {getLogfireUrl() && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Logfire Trace:</span>
              <a
                href={getLogfireUrl()!}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
                View trace
                <svg
                  className="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </a>
            </div>
          )}

          {/* Status at bottom */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Status:</span>
            {canChangeStatus ? (
              <>
                <div className="relative">
                  <select
                    value={session.status}
                    onChange={(e) =>
                      statusMutation.mutate(e.target.value as SessionStatus)
                    }
                    disabled={statusMutation.isPending}
                    className={`appearance-none pl-3 pr-7 py-1 rounded-full text-xs font-medium cursor-pointer border-0 outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-300 disabled:opacity-60 disabled:cursor-not-allowed ${statusColors[session.status]}`}>
                    {ALL_STATUSES.map((s) => {
                      const isEditable = EDITABLE_STATUSES.has(s)
                      // Show all statuses, but only allow changing to the editable set.
                      // If current status is non-editable (e.g. 'completed'), keep it selected but disabled.
                      const disabled =
                        !isEditable && s !== (session.status as SessionStatus)
                      return (
                        <option key={s} value={s} disabled={disabled}>
                          {STATUS_LABELS[s] ?? s}
                        </option>
                      )
                    })}
                  </select>
                  <svg
                    className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 opacity-60"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
                {isRetryableSessionStatus(session.status) && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    disabled={
                      retryMutation.isPending || statusMutation.isPending
                    }
                    onClick={() => retryMutation.mutate()}>
                    {retryMutation.isPending ? 'Retrying...' : 'Retry'}
                  </Button>
                )}
                {statusMutation.isError && (
                  <span className="text-xs text-red-500">Failed to update</span>
                )}
              </>
            ) : (
              <span
                className={`pl-3 pr-3 py-1 rounded-full text-xs font-medium ${statusColors[session.status]}`}>
                {STATUS_LABELS[session.status] ?? session.status}
              </span>
            )}
          </div>

          <SessionMetadata
            metadata={session.metadata}
            excludedKeys={headerMetadataKeys}
          />
        </div>

        {(factsEntries.length > 0 || session?.facts) && (
          <div className="mb-8 space-y-3">
            <h2 className="text-xl font-semibold">Facts</h2>
            {factsEntries.length > 0 ? (
              <SessionDataTree entries={factsEntries} />
            ) : (
              <div className="text-xs text-gray-900 whitespace-pre-wrap">
                {session?.facts}
              </div>
            )}
          </div>
        )}

        {/* Sits below Facts and stands on its own: a run can end with extracts
            and no input facts, so this is not nested in the block above. */}
        <SessionExtracts extracts={session?.extracts ?? null} />

        {attemptlessVideoUrl && (
          <div className="mb-8 space-y-3">
            <h2 className="text-xl font-semibold">Recording (Video)</h2>
            <video
              aria-label="Session recording"
              controls
              className="w-full rounded border border-gray-200 bg-black"
              src={attemptlessVideoUrl}
            />
          </div>
        )}

        {/* Main Content - Latest Attempt + Previous Attempts */}
        <div>
          {latestAttempt && (
            <div id={latestAttempt.id}>
              {(() => {
                const latestAttemptReports = reportsByAttempt.get(
                  latestAttempt.id
                )
                return (
                  <>
                    <h2 className="text-xl font-semibold mb-4">
                      Most Recent Attempt
                    </h2>
                    <div className="mb-4">
                      {renderAttemptTopInfo(latestAttempt)}
                    </div>

                    {latestAttemptReports && (
                      <div className="mb-6">
                        <UnexpectedErrorReportAction
                          reports={latestAttemptReports}
                          onOpen={() =>
                            openUnexpectedErrorReports(
                              latestAttempt.id,
                              latestAttemptReports
                            )
                          }
                        />
                      </div>
                    )}

                    {latestAttempt.result &&
                      latestAttempt.result !== 'success' &&
                      latestAttempt.error && (
                        <div className="mb-6 rounded border border-red-200 bg-red-50 p-3 attempt-error">
                          <div className="text-xs font-semibold text-red-700 mb-1">
                            Error
                          </div>
                          <div className="text-sm text-red-800 whitespace-pre-wrap">
                            {latestAttempt.error}
                          </div>
                        </div>
                      )}

                    {session.structured_report ? (
                      <StructuredRunReport
                        report={session.structured_report as StructuredReport}
                      />
                    ) : (
                      latestAttempt.report && (
                        <div className="markdown-content attempt-report">
                          <ReactMarkdown components={markdownComponents}>
                            {latestAttempt.report}
                          </ReactMarkdown>
                        </div>
                      )
                    )}

                    {latestAttemptVideoUrl && (
                      <div className="mt-8">
                        <h3 className="text-lg font-semibold mb-3">
                          Recording (Video)
                        </h3>
                        <video
                          controls
                          className="w-full rounded border border-gray-200 bg-black"
                          src={latestAttemptVideoUrl}
                        />
                      </div>
                    )}

                    {/* Screenshots for latest attempt */}
                    {canSeeScreenshots && (
                      <div className="mt-12">
                        <h3 className="text-lg font-semibold mb-6">
                          Attempt Screenshots
                        </h3>
                        <AttemptScreenshotsList
                          sessionId={session.id}
                          attemptId={latestAttempt.id}
                          getToken={getToken}
                        />
                      </div>
                    )}
                  </>
                )
              })()}
            </div>
          )}

          {previousAttempts.length > 0 && (
            <div className="mt-12">
              <h2 className="text-xl font-semibold mb-4">
                Previous Attempts ({previousAttempts.length})
              </h2>
              <div className="space-y-6">
                {previousAttempts.map((attempt, index) => {
                  const isExpanded = expandedPreviousAttemptIds.has(attempt.id)
                  const attemptVideoUrl = data?.media?.[attempt.id]?.video?.url
                  const attemptLogfireUrl = getAttemptLogfireUrl(attempt)
                  const attemptReports = reportsByAttempt.get(attempt.id)
                  return (
                    <div
                      key={attempt.id}
                      id={attempt.id}
                      className="rounded-lg border border-gray-200 bg-white">
                      <div className="w-full px-4 py-3">
                        <div className="group flex items-start gap-3 text-xs text-gray-600">
                          <button
                            type="button"
                            onClick={() =>
                              setExpandedPreviousAttemptIds((prev) => {
                                const next = new Set(prev)
                                if (next.has(attempt.id)) {
                                  next.delete(attempt.id)
                                } else {
                                  next.add(attempt.id)
                                }
                                return next
                              })
                            }
                            className="flex min-h-6 min-w-6 items-center justify-center rounded hover:bg-gray-100"
                            aria-expanded={isExpanded}
                            aria-controls={`${attempt.id}-details`}
                            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} previous attempt ${index + 1}`}>
                            <svg
                              className={`w-4 h-4 text-gray-400 transition-transform ${
                                isExpanded ? 'rotate-90' : ''
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </button>
                          <div className="flex-1">
                            {renderAttemptTopInfo(attempt, {
                              includeLogfireTrace: false,
                            })}
                          </div>
                        </div>
                      </div>

                      {attemptReports && (
                        <div className="px-4 pb-3 pl-11">
                          <UnexpectedErrorReportAction
                            reports={attemptReports}
                            onOpen={() =>
                              openUnexpectedErrorReports(
                                attempt.id,
                                attemptReports
                              )
                            }
                          />
                        </div>
                      )}

                      <div id={`${attempt.id}-details`}>
                        {isExpanded && (
                          <div className="px-4 pb-4">
                            <div className="mt-2 mb-3 flex items-center gap-2">
                              <span className="text-xs text-gray-500 w-24 shrink-0">
                                Logfire Trace:
                              </span>
                              {attemptLogfireUrl ? (
                                <a
                                  href={attemptLogfireUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
                                  View trace
                                  <svg
                                    className="w-3 h-3"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                    />
                                  </svg>
                                </a>
                              ) : (
                                <span className="text-xs text-gray-500">-</span>
                              )}
                            </div>
                            {attempt.result &&
                              attempt.result !== 'success' &&
                              attempt.error && (
                                <div className="mb-3 rounded border border-red-200 bg-red-50 p-2">
                                  <div className="text-xs text-red-800 whitespace-pre-wrap">
                                    {attempt.error}
                                  </div>
                                </div>
                              )}

                            {attempt.report ? (
                              <div className="markdown-content">
                                <ReactMarkdown components={markdownComponents}>
                                  {attempt.report}
                                </ReactMarkdown>
                              </div>
                            ) : (
                              <div className="text-sm text-gray-500">
                                No report available.
                              </div>
                            )}

                            {attemptVideoUrl && (
                              <div className="mt-8">
                                <h3 className="text-lg font-semibold mb-3">
                                  Recording (Video)
                                </h3>
                                <video
                                  controls
                                  className="w-full rounded border border-gray-200 bg-black"
                                  src={attemptVideoUrl}
                                />
                              </div>
                            )}
                            <details className="mt-8 rounded border border-gray-200 bg-gray-50">
                              <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium text-gray-700">
                                Attempt Screenshots
                              </summary>
                              <div className="px-4 pb-4 space-y-8">
                                <AttemptScreenshotsList
                                  sessionId={session.id}
                                  attemptId={attempt.id}
                                  getToken={getToken}
                                />
                              </div>
                            </details>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {!hasAttempts && canSeeAttempts && (
            <div>
              <h2 className="text-xl font-semibold mb-4">Session Details</h2>
              <div className="mb-4 flex flex-wrap items-center gap-3 text-xs text-gray-600">
                <span>Started: {formatDateTime(session.started_at)}</span>
                <span>Completed: {formatDateTime(session.completed_at)}</span>
                <span className="font-semibold text-gray-900">
                  Duration:{' '}
                  {formatDuration(session.started_at, session.completed_at)}
                </span>
              </div>

              {session.structured_report ? (
                <StructuredRunReport
                  report={session.structured_report as StructuredReport}
                />
              ) : (
                session.run_report && (
                  <div className="markdown-content">
                    <ReactMarkdown components={markdownComponents}>
                      {session.run_report}
                    </ReactMarkdown>
                  </div>
                )
              )}

              {legacyScreenshots.length > 0 && (
                <div className="mt-12">
                  <h3 className="text-lg font-semibold mb-6">
                    Session Screenshots ({legacyScreenshots.length})
                  </h3>
                  <div className="space-y-8">
                    {legacyScreenshots.map((screenshot, index) => (
                      <div
                        key={index}
                        className="pb-8 border-b border-gray-200 last:border-b-0">
                        <div className="text-sm text-gray-500 mb-3">
                          <span className="font-medium">
                            #{screenshot.sequence + 1}
                          </span>
                          <span className="mx-2">•</span>
                          <span className="font-mono text-xs">
                            {screenshot.name}
                          </span>
                        </div>
                        <LazyImage
                          src={screenshot.url}
                          alt={`Screenshot ${screenshot.sequence + 1}`}
                          className="max-w-full"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Empty state when there is no attempt or session content */}
          {!hasAttempts &&
            canSeeAttempts &&
            !session.run_report &&
            legacyScreenshots.length === 0 &&
            !session.started_at &&
            !session.completed_at && (
              <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                  <div className="text-sm text-gray-500">
                    Session pending... Angelica will begin working shortly.
                  </div>
                </div>
              </div>
            )}
        </div>
      </div>
      <UnexpectedErrorReportsSheet
        attemptId={reportAttemptId}
        reports={selectedAttemptReports}
        open={reportAttemptId !== null}
        onOpenChange={(open) => {
          if (!open) setReportAttemptId(null)
        }}
      />
    </div>
  )
}
